<script>
/**
 * Type         : Modal component
 * Title        : 오더 내 아이템 입력 컴포넌트
 * Description  : 오더 내 아이템 입력하기 위한 컴포넌트
 * 
 * Input
 */

export default {
  name: "Order-Item-Regi-Form-Unit",
  props: {
    item  : Object, // 배송상품 정보
    showBarcode: {
      type: Boolean,
      default: false
    },
    partnerId: {
      type: [String, Number],
      default: ''
    },
    enableHsCodeAuto: {
      type: Boolean,
      default: false
    }
  },
  emits: ["remove-row", "update-item"], // 행 삭제 이벤트
  data() {
    return {
      localItem: { ...this.item },
      hsCodeResolveTimer: null,
      lastAutoHsCode: '',
      lastResolvedItemName: ''
    }
  },
  watch: {
    item: {
      handler(newVal) {
        this.localItem = { ...newVal }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    calcPrice () {
      return this.localItem.unitPrice * this.localItem.itemQty
    }
  },
  methods : {
    shouldOverwriteHsCode() {
      const currentHsCode = String(this.localItem.hsCode || '').trim()
      return !currentHsCode || currentHsCode === this.lastAutoHsCode
    },

    scheduleHsCodeResolve() {
      if (this.hsCodeResolveTimer) clearTimeout(this.hsCodeResolveTimer)
      this.hsCodeResolveTimer = setTimeout(() => {
        this.resolveHsCode()
      }, 1000)
    },

    async resolveHsCode() {
      if (this.hsCodeResolveTimer) {
        clearTimeout(this.hsCodeResolveTimer)
        this.hsCodeResolveTimer = null
      }

      const itemName = String(this.localItem.itemName || '').trim()
      if (!this.enableHsCodeAuto) return
      if (!itemName || itemName === this.lastResolvedItemName || !this.shouldOverwriteHsCode()) return

      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/parcel/resolveHsCode',
          data: { itemName, partnerId: this.partnerId },
          name: '[ Axios / HS CODE 자동 조회 ]'
        })

        const hsCode = String(res?.data?.hsCode || '').trim()
        if (!res?.data?.matched || !/^\d{6}$/.test(hsCode) || !this.shouldOverwriteHsCode()) return

        this.localItem.hsCode = hsCode
        this.lastAutoHsCode = hsCode
        this.lastResolvedItemName = itemName
        this.emitItemUpdate()
      } catch (error) {
        this.$log?.debug?.('HS CODE 자동 조회 실패', error)
      }
    },

    /**
     * Title        : 바코드 숫자 정규화
     * Status       : Actived
     * Description  : 바코드 입력값에서 숫자 외 문자를 즉시 제거한다.
     * @param value
     */
    sanitizeBarcode(value) {
      const normalized = String(value ?? '').replace(/\D+/g, '')
      if (this.localItem.barcode !== normalized) {
        this.localItem.barcode = normalized
      }
      this.emitItemUpdate()
    },
    emitItemUpdate() {
      this.$emit("update-item", { ...this.localItem })
    },

    handleItemNameUpdate() {
      this.emitItemUpdate()
      this.scheduleHsCodeResolve()
    },

    /**
     * Title        : 행 삭제
     * Status       : Actived
     * Description  : 행 삭제
     * @param row 
     */
    removeThisItem(row) {
      this.$emit("remove-row", row)
    },


    /*
     * Title        : 입력값 체크
     * Status       : Actived
     * Description  : 입력값 체크
     */
    checkItemData(){
      let result = []
      let empty = true
      let valid = true

      let refList = [
        this.$refs.itemNameRef,
        this.$refs.brandRef,
        this.$refs.itemQtyRef,
        this.$refs.unitPriceRef,
      ]

      // 품명
      result = this.$checkEmptyRefList(refList)
      if(result.length > 0){
        empty = false
        return { empty: empty, valid: valid, result: result}
      }

      result = this.$checkInvalidRefList(refList)
      if(result.length > 0){
        valid = false
        return { empty: empty, valid: valid, result: result}
      }
      return { empty: true, valid: true, result: [] }
    }

    

  },
  mounted(){
  },
  beforeUnmount() {
    if (this.hsCodeResolveTimer) clearTimeout(this.hsCodeResolveTimer)
  }
}
</script>


<template>
  
  <q-card
    class="modal-regi-card">

    <q-card-section>
      <div class="row">
        <div class="col">
          <div class="text-h6">
            상품정보
          </div>
        </div>
        <div class="col text-right">
          <q-btn
            dense
            flat
            color="negative"
            icon="delete"
            @click="removeThisItem(item)" />
        </div>
      </div>
    </q-card-section>


    <q-card-section class="q-pt-none">
      <div 
        class="row q-mb-sm q-gutter-sm">
        <div class="col">
          <q-input
            v-if="showBarcode"
            v-model="localItem.barcode"
            label="바코드"
            inputmode="numeric"
            @update:model-value="sanitizeBarcode"
            placeholder="바코드를 입력해 주세요."
            :rules="[val => { const sanitized = String(val || '').replace(/\D+/g, ''); return !sanitized || /^\d+$/.test(sanitized) || '바코드는 숫자만 입력 가능합니다.'; }]" />
          <q-input 
            v-model="localItem.itemName"
            ref="itemNameRef"
            label="품명"
            placeholder="상품명을 적어주세요."
            @update:model-value="handleItemNameUpdate"
            @blur="resolveHsCode"
            :rules="[this.$validationSet.itemName]" />
          <q-input 
            v-model="localItem.brand"
            ref="brandRef"
            label="브랜드"
            placeholder="브랜드명을 적어주세요."
            @update:model-value="emitItemUpdate"
            :rules="[this.$validationSet.alphaNum]" />
        </div>
      </div>
      <div class="row q-gutter-sm">
        <div class="col">
          <q-input 
            v-model="localItem.itemQty"
            ref="itemQtyRef"
            input-class="text-right"
            type="number" 
            maxlength="5"
            min="1"
            @update:model-value="emitItemUpdate"
            label="수량" />    
        </div>
        <div class="col">
          <q-input 
            v-model="localItem.unitPrice"
            ref="unitPriceRef"
            label="단가"
            type="number" 
            step="any"
            min="0"
            @update:model-value="emitItemUpdate"
            input-class="text-right" />
        </div>
        <div class="col">
          <q-input 
            v-model="calcPrice"
            label="상품가격"
            type="number" 
            min="0"
            readonly
            input-class="text-right" />
        </div>
        <div class="col">
          <q-input
            v-model="localItem.weight"
            ref="weightRef"
            label="무게(kg)"
            type="number"
            step="any"
            min="0.001"
            @update:model-value="emitItemUpdate"
            input-class="text-right" />
        </div>
        <div v-if="enableHsCodeAuto" class="col">
          <q-input
            v-model="localItem.hsCode"
            label="HS CODE"
            maxlength="6"
            inputmode="numeric"
            @update:model-value="emitItemUpdate"
            :rules="[
              val => !val || /^\\d{6}$/.test(String(val)) || 'HS CODE는 숫자 6자리입니다.'
            ]" />
        </div>
      </div>
    </q-card-section>
  </q-card>

</template>

<style scoped lang="scss">
.modal-regi-card {
  width: 100%;
  min-width: 300px;
  max-width: 400px;
}
</style>
